/**
 * The rotation sheet. The records desk keeps it; the web desk reads it;
 * nobody updates it until an edition forces the issue. One row per
 * edition rotation: where the three legs lived, and the assembly order
 * for that edition's seal. The 41.2 row is current. Older rows are
 * correct for their editions and WRONG for this one — the desk notes
 * from 2025 floating around describe the 40.9 row and get even that
 * order wrong. The sheet wins. It always wins.
 */

export interface LegPositions {
  /** which beacon salt generation is live */
  beacon: string;
  /** which strings table carries the un-translated greeting */
  strings: string;
  /** which plate ink is current (the constant, not a ledger row) */
  plate: string;
}

export interface RotationRow {
  edition: string;
  rotatedOn: string;
  legs: LegPositions;
  /** assembly order for this edition, leg sources by name */
  order: string[];
  status: "current" | "superseded";
  note: string;
}

export const ROTATION_SHEET: RotationRow[] = [
  {
    edition: "39.4",
    rotatedOn: "2025-08-30",
    legs: { beacon: "beacon salt v1 (retired)", strings: "A-30 strings, reader.welcome slot", plate: "plate ledger row 39.4" },
    order: ["plate", "beacon", "strings"],
    status: "superseded",
    note: "archive-only edition; order recorded for replay",
  },
  {
    edition: "40.1",
    rotatedOn: "2026-01-12",
    legs: { beacon: "beacon salt v1 (retired)", strings: "C-04 strings, reader.greeting.formal", plate: "plate ledger row 40.1" },
    order: ["strings", "plate", "beacon"],
    status: "superseded",
    note: "redesign retired the old plates",
  },
  {
    edition: "40.9",
    rotatedOn: "2026-02-27",
    legs: { beacon: "beacon salt v2 (retired)", strings: "C-04 strings, reader.greeting.formal", plate: "plate ledger row 41.1" },
    order: ["ink", "strings", "beacon"],
    status: "superseded",
    note: "the 2025 desk-notes sheet describes this row, wrongly",
  },
  {
    edition: "41.1",
    rotatedOn: "2026-06-02",
    legs: { beacon: "beacon salt v2 (retired)", strings: "C-04 strings, reader.greeting.formal", plate: "PLATE_INK (current constant)" },
    order: ["beacon", "plate", "strings"],
    status: "superseded",
    note: "march rotation moved the beacon; B-11 strings dump landed after",
  },
  {
    edition: "41.2",
    rotatedOn: "2026-03-14",
    legs: { beacon: "BEACON_SALT_V3 (analytics, post-march rotation)", strings: "B-11 strings table, reader.greeting.formal", plate: "PLATE_INK (current constant, legacy shelf)" },
    order: ["beacon", "strings", "plate"],
    status: "current",
    note: "order changes every rotation on purpose; the sheet is the only record",
  },
];

export function rowFor(edition: string): RotationRow | null {
  return ROTATION_SHEET.find((r) => r.edition === edition) ?? null;
}

export function currentRow(): RotationRow {
  const row = ROTATION_SHEET.find((r) => r.status === "current");
  if (!row) throw new Error("rotation sheet has no current row; the records desk has some explaining to do");
  return row;
}

/** QA: exactly one current row, ever. */
export function sheetSane(): boolean {
  return ROTATION_SHEET.filter((r) => r.status === "current").length === 1;
}
